/* eslint-disable simple-import-sort/imports */
import React, { useEffect, useRef } from 'react';
import { useInView } from 'framer-motion';
import { styled } from 'styled-components';

import { Reveal, Stagger, StaggerItem } from 'pages/HomeV2/components/Motion';
import {
  Container,
  Eyebrow,
  Lead,
  SectionHead,
  Title,
  v2,
} from 'pages/HomeV2/v2';

import { goodieFeatures } from '../data-goodie';
import { ICONS } from '../icons';
import { GOODIE_EVENTS, trackGoodieEvent } from '../tracking';

const Wrapper = styled.section`
  position: relative;
  background: ${v2.bgSoft};
  border-top: 1px solid ${v2.border};
  padding: clamp(72px, 9vw, 130px) 0;
`;

const Grid = styled(Stagger)`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;

  @media (min-width: 640px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 20px;
  }

  @media (min-width: 1024px) {
    grid-template-columns: repeat(6, minmax(0, 1fr));
  }
`;

/* 5 cards: on desktop the first two span 3 columns each, the remaining
   three span 2 each, so both rows are full. */
const Cell = styled(StaggerItem)`
  display: flex;
  min-width: 0;

  @media (min-width: 640px) and (max-width: 1023px) {
    &:first-child {
      grid-column: span 2;
    }
  }

  @media (min-width: 1024px) {
    grid-column: span 2;

    &:nth-child(-n + 2) {
      grid-column: span 3;
    }
  }
`;

const Card = styled.article`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 18px;
  width: 100%;
  padding: 26px;
  border-radius: 22px;
  border: 1px solid ${v2.border};
  background: ${v2.surface};
  backdrop-filter: blur(10px);
  transition:
    transform 0.35s cubic-bezier(0.21, 0.47, 0.32, 0.98),
    border-color 0.35s ease,
    background 0.35s ease,
    box-shadow 0.35s ease;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    background: radial-gradient(
      70% 50% at 100% 0%,
      ${({ $accent }) =>
        $accent === 'orange'
          ? 'rgba(255, 159, 79, 0.14)'
          : 'rgba(30, 150, 252, 0.14)'},
      transparent 60%
    );
    opacity: 0;
    transition: opacity 0.35s ease;
    pointer-events: none;
  }

  h3 {
    font-family: ${v2.fontDisplay};
    font-size: clamp(1.15rem, 1.5vw, 1.32rem);
    font-weight: 700;
    letter-spacing: -0.02em;
    line-height: 1.25;
    color: ${v2.text};
    margin: 0;
  }

  p {
    font-family: ${v2.fontBody};
    font-size: 15px;
    line-height: 1.62;
    color: ${v2.textMuted};
    margin: 0;
  }

  @media (hover: hover) {
    &:hover {
      transform: translateY(-4px);
      border-color: ${v2.borderStrong};
      background: ${v2.surfaceHover};
      box-shadow: 0 22px 50px rgba(0, 0, 0, 0.35);

      &::before {
        opacity: 1;
      }
    }
  }

  @media (min-width: 1024px) {
    padding: 30px;
  }
`;

const IconBox = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
  border-radius: 14px;
  background: ${({ $accent }) =>
    $accent === 'orange' ? v2.orangeSoft : v2.blueSoft};
  color: ${({ $accent }) => ($accent === 'orange' ? v2.orange : '#5bb8ff')};
  border: 1px solid
    ${({ $accent }) =>
      $accent === 'orange'
        ? 'rgba(255, 159, 79, 0.3)'
        : 'rgba(30, 150, 252, 0.3)'};

  svg {
    width: 24px;
    height: 24px;
  }
`;

const FeaturedTag = styled.span`
  position: absolute;
  top: 22px;
  right: 22px;
  padding: 5px 10px;
  border-radius: 100px;
  border: 1px solid rgba(255, 159, 79, 0.4);
  background: ${v2.orangeSoft};
  font-family: ${v2.fontBody};
  font-size: 11.5px;
  font-weight: 700;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  color: ${v2.orange};
`;

const GoodieFeatures = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.2 });
  const tracked = useRef(false);

  // "goodie_feature_scroll": fired once, as soon as the feature grid is
  // actually on screen – whether the visitor scrolled or used the hero CTA.
  useEffect(() => {
    if (!inView || tracked.current) return;
    tracked.current = true;
    trackGoodieEvent(GOODIE_EVENTS.featureScroll);
  }, [inView]);

  return (
    <Wrapper id="features" aria-labelledby="goodie-features-title">
      <Container>
        <SectionHead $left>
          <Reveal>
            <Eyebrow>Funktionen</Eyebrow>
          </Reveal>
          <Reveal delay={0.1}>
            <Title id="goodie-features-title">
              Ein <span className="grad">FOXI</span>. Viele Aufgaben.
            </Title>
          </Reveal>
          <Reveal delay={0.2}>
            <Lead>
              Fünf Bereiche, ein System – entwickelt für den Alltag in
              Zahnarztpraxen.
            </Lead>
          </Reveal>
        </SectionHead>

        {/* Plain div carries the in-view ref: Stagger does not forward refs. */}
        <div ref={ref}>
          <Grid gap={0.08}>
            {goodieFeatures.map((feature) => {
              const Icon = ICONS[feature.icon];
              return (
                <Cell key={feature.title} y={28}>
                  <Card $accent={feature.accent}>
                    {feature.featured && (
                      <FeaturedTag>Kernfunktion</FeaturedTag>
                    )}
                    <IconBox $accent={feature.accent}>
                      <Icon />
                    </IconBox>
                    <h3>{feature.title}</h3>
                    <p>{feature.description}</p>
                  </Card>
                </Cell>
              );
            })}
          </Grid>
        </div>
      </Container>
    </Wrapper>
  );
};

export default GoodieFeatures;
